import { MarketSignal, Holding, MacroState, MacroIndicators } from '../types';
import { DASHBOARD_CONFIG } from '../constants/dashboard';

export const DEFAULT_NEUTRAL_MACRO: MacroState = {
  asOfDate: new Date().toISOString().split('T')[0],
  regimes: {
    liquidity: 'Neutral',
    credit: 'Flat',
    policy: 'Normal',
  },
  riskMetrics: {
    riskScore: DASHBOARD_CONFIG.DEFAULT_RISK_SCORE,
    macroWeight: DASHBOARD_CONFIG.DEFAULT_MACRO_WEIGHT,
    portfolioRiskBudgetPct: DASHBOARD_CONFIG.DEFAULT_RISK_BUDGET,
    trimThresholdPct: DASHBOARD_CONFIG.DEFAULT_TRIM_THRESHOLD,
    updatedAt: new Date().toISOString(),
  },
  aiSummary: '',
  aiIntelligence: '',
  aiStrategies: [],
  fedFunds: { value: 0, date: '' },
  inflation: { value: 0, date: '' },
  unemployment: { value: 0, date: '' }
};

export const getStandardMarketSession = (date: Date = new Date()): MarketSignal['marketState'] => {
  // 🌐 미국 동부 시각(America/New_York)으로 변환
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
    weekday: 'short'
  });
  
  try {
    const parts = formatter.formatToParts(date);
    const getPart = (type: string) => parts.find(p => p.type === type)?.value || '';
    
    const hour = parseInt(getPart('hour'));
    const minute = parseInt(getPart('minute'));
    const weekday = getPart('weekday');
    
    if (weekday === 'Sat' || weekday === 'Sun') return 'CLOSED';
    
    const totalMinutes = hour * 60 + minute;
    if (totalMinutes >= 240 && totalMinutes < 570) return 'PRE';
    if (totalMinutes >= 570 && totalMinutes < 960) return 'REGULAR';
    if (totalMinutes >= 960 && totalMinutes < 1200) return 'POST';
    
    return 'CLOSED';
  } catch (e) {
    console.warn('[MarketClock] TZ conversion failed, defaulting to CLOSED', e);
    return 'CLOSED';
  }
};

export const calculateSignal = (
  symbol: string,
  price: number,
  prevClose: number,
  shortRatio: number,
  daysToCover: number,
  shortInterestChangePct: number,
  macro: MacroState,
  optionsData?: {
    putCallRatio?: number;
    ivRank?: number | null;
    gammaFlip?: number;
    maxPain?: number;
    rvol?: number;
    volumeScore?: number;
    cmf?: number;
    volumeSignalType?: MarketSignal['volumeSignalType'];
    avgVolume?: number;
  },
  holding?: Holding,
  totalPortfolioValue?: number,
  timestamp?: string,
  marketState?: MarketSignal['marketState'],
  name?: string
): MarketSignal => {
  if (['CASH', 'USD', 'KRW'].includes(symbol.toUpperCase())) {
    return {
      symbol: symbol.toUpperCase(),
      price: 1,
      prevClose: 1,
      pctChange: 0,
      shortRatio: 0,
      daysToCover: 0,
      putCallRatio: 0,
      ivRank: null,
      gammaFlip: 0,
      maxPain: 0,
      totalScore: 0,
      alertLevel: 'NORMAL',
      portfolioAction: 'HOLD',
      proposedTradeShares: 0,
      marketState: 'REGULAR'
    };
  }

  const pctChange = (prevClose && prevClose !== 0) ? ((price - prevClose) / prevClose) * 100 : 0;

  // Logic from n8n workflow "Build Score Data v4"
  const shortFlow = (shortRatio >= 0.6 ? 35 : shortRatio >= 0.5 ? 25 : shortRatio >= 0.4 ? 12 : 0);
  const shortStock = (daysToCover >= 5 ? 25 : daysToCover >= 3 ? 15 : 0) + (shortInterestChangePct >= 10 ? 15 : shortInterestChangePct >= 3 ? 8 : 0);
  const priceStress = pctChange <= -5 ? 18 : pctChange <= -3 ? 12 : pctChange >= 5 ? 15 : pctChange >= 3 ? 9 : 0;
  
  // Options Score (Only if data is available)
  let options = 0;
  let positioning = 0;
  let gammaFlipVal = 0;
  let putCallRatioVal = 0;
  let ivRankVal: number | null = null;
  let maxPainVal = 0;

  const hasOptions = optionsData && (
    (optionsData.putCallRatio !== undefined && optionsData.putCallRatio > 0) ||
    (optionsData.ivRank !== undefined && optionsData.ivRank !== null && optionsData.ivRank > 0) ||
    (optionsData.gammaFlip !== undefined && optionsData.gammaFlip !== 0) ||
    (optionsData.maxPain !== undefined && optionsData.maxPain > 0)
  );

  if (hasOptions && optionsData) {
    putCallRatioVal = optionsData.putCallRatio || 0;
    ivRankVal = (optionsData.ivRank !== undefined && optionsData.ivRank !== null && optionsData.ivRank > 0)
      ? optionsData.ivRank
      : null;
    gammaFlipVal = optionsData.gammaFlip || 0;
    maxPainVal = optionsData.maxPain || 0;

    const effectiveIvForScore = ivRankVal !== null ? ivRankVal : 50;
    options = (putCallRatioVal >= DASHBOARD_CONFIG.PUT_CALL_RATIO_BEARISH_THRESHOLD ? 12 : putCallRatioVal <= DASHBOARD_CONFIG.PUT_CALL_RATIO_BULLISH_THRESHOLD ? 9 : 0) + 
              (effectiveIvForScore >= DASHBOARD_CONFIG.IV_RANK_EXTREME_THRESHOLD ? 12 : effectiveIvForScore >= DASHBOARD_CONFIG.IV_RANK_ELEVATED_THRESHOLD ? 8 : 0);
    
    const gammaDistancePct = price > 0 && gammaFlipVal > 0 ? (Math.abs(price - gammaFlipVal) / price) * 100 : null;
    if (gammaDistancePct !== null && gammaDistancePct <= 2) positioning += 20;
    else if (gammaDistancePct !== null && gammaDistancePct <= 5) positioning += 10;
  }

  const baseScore = shortFlow + shortStock + priceStress + options + positioning;

  // Macro Overlay
  let macroScale = 1 - ((macro.riskMetrics?.riskScore || macro.riskScore || DASHBOARD_CONFIG.RISK_BASELINE) - DASHBOARD_CONFIG.RISK_BASELINE) / 200;
  if (macro.regimes.liquidity === 'Tightening') macroScale -= 0.08;
  if (macro.regimes.liquidity === 'Easing') macroScale += 0.05;
  if (macro.regimes.credit === 'Contracting') macroScale -= 0.05;

  const totalScore = Math.round(baseScore * macroScale);

  let alertLevel: MarketSignal['alertLevel'] = 'NORMAL';
  if (totalScore >= DASHBOARD_CONFIG.ALERT_CRITICAL_THRESHOLD) alertLevel = 'CRITICAL';
  else if (totalScore >= DASHBOARD_CONFIG.ALERT_HIGH_THRESHOLD) alertLevel = 'HIGH';
  else if (totalScore >= DASHBOARD_CONFIG.ALERT_ELEVATED_THRESHOLD) alertLevel = 'ELEVATED';
  else if (totalScore >= DASHBOARD_CONFIG.ALERT_WATCH_THRESHOLD) alertLevel = 'WATCH';

  let portfolioAction: MarketSignal['portfolioAction'] = 'HOLD';
  let proposedTradeShares = 0;

  if (holding) {
    if (alertLevel === 'CRITICAL') {
      portfolioAction = 'EXIT';
      proposedTradeShares = -holding.shares;
    }
    else if (alertLevel === 'HIGH') {
      portfolioAction = 'REDUCE';
      if (totalPortfolioValue && holding.targetWeight !== undefined) {
        const targetShares = Math.floor((totalPortfolioValue * holding.targetWeight) / price);
        const halfShares = Math.floor(holding.shares / 2);
        proposedTradeShares = Math.min(targetShares - holding.shares, -halfShares);
        if (proposedTradeShares >= 0) proposedTradeShares = -halfShares;
      } else {
        proposedTradeShares = -Math.floor(holding.shares / 2);
      }
    }
    else if (totalScore < 20 && holding.weight < holding.targetWeight) {
      portfolioAction = 'ADD';
      if (totalPortfolioValue) {
        const targetShares = Math.floor((totalPortfolioValue * holding.targetWeight) / price);
        proposedTradeShares = targetShares - holding.shares;
        if (proposedTradeShares <= 0) portfolioAction = 'HOLD';
      }
    }
  } else {
    // 관심 종목 (보유 자산이 아님)
    if (alertLevel === 'CRITICAL' || alertLevel === 'HIGH' || alertLevel === 'ELEVATED') {
      portfolioAction = 'WATCH';
    } else if (totalScore < 20) {
      portfolioAction = 'BUY';
    } else {
      portfolioAction = 'HOLD';
    }
  }
  
  const isCash = ['CASH', 'USD', 'KRW'].includes(symbol.toUpperCase());
  
  // Detection for "Incomplete" data (triggers "Syncing" UI state)
  const isPending = !isCash && (price === 0);

  return {
    symbol,
    name: name || holding?.name,
    price,
    prevClose,
    pctChange,
    shortRatio,
    daysToCover,
    putCallRatio: putCallRatioVal,
    ivRank: ivRankVal,
    gammaFlip: gammaFlipVal,
    maxPain: maxPainVal,
    totalScore,
    alertLevel,
    portfolioAction,
    proposedTradeShares,
    isPending,
    timestamp,
    marketState: marketState || 'REGULAR',
    market: holding?.market || (symbol.endsWith('.KS') || symbol.endsWith('.KQ') ? 'KR' : 'US'),
    rvol: optionsData?.rvol !== undefined ? Number(optionsData.rvol) : 1.0,
    volumeScore: optionsData?.volumeScore !== undefined ? Number(optionsData.volumeScore) : 50.0,
    cmf: optionsData?.cmf !== undefined ? Number(optionsData.cmf) : 0.0,
    volumeSignalType: optionsData?.volumeSignalType || 'NORMAL',
    avgVolume: optionsData?.avgVolume !== undefined ? Number(optionsData.avgVolume) : 0
  };
};

const getPreviousIndicators = (indicators: MacroIndicators): MacroIndicators => {
  if (!indicators) return indicators;
  const prev = JSON.parse(JSON.stringify(indicators)) as MacroIndicators;
  
  const shift = (obj: any) => {
    if (obj && typeof obj.value === 'number') {
      const val = obj.value;
      const chg = typeof obj.change === 'number' ? obj.change : 0;
      if (chg === 0) return;
      if (val > 50) {
        obj.value = val / (1 + chg / 100);
      } else {
        obj.value = val - chg;
      }
    }
  };

  if (prev.vix) shift(prev.vix);
  if (prev.vkospi) shift(prev.vkospi);
  if (prev.yieldSpread) shift(prev.yieldSpread);
  if (prev.usdkrw) shift(prev.usdkrw);
  if (prev.fedFunds) shift(prev.fedFunds);
  if (prev.inflation) shift(prev.inflation);
  if (prev.unemployment) shift(prev.unemployment);
  if (prev.kospi) shift(prev.kospi);
  if (prev.kosdaq) shift(prev.kosdaq);
  if (prev.sp500) shift(prev.sp500);
  if (prev.nasdaq) shift(prev.nasdaq);
  
  if (prev.government) {
    if (prev.government.krYieldSpread) shift(prev.government.krYieldSpread);
    if (prev.government.krCreditSpread) shift(prev.government.krCreditSpread);
    if (prev.government.krExports) shift(prev.government.krExports);
    if (prev.government.krCpi) shift(prev.government.krCpi);
    if (prev.government.bokRate) shift(prev.government.bokRate);
    if (prev.government.treasury10Y) shift(prev.government.treasury10Y);
    if (prev.government.treasury2Y) shift(prev.government.treasury2Y);
  }
  
  return prev;
};

export const calculateMacroParameters = (
  indicators: MacroIndicators, 
  market: 'US' | 'KR' = 'US',
  isPrevCalculation = false
): Partial<MacroState> => {
  const isKr = market === 'KR';
  const spreadValue = isKr
    ? indicators.government?.krYieldSpread?.value
    : indicators.yieldSpread?.value;
  const spreadSource = isKr ? 'krYieldSpread (KTB 10Y-3Y)' : 'T10Y2Y (10Y-2Y)';
  if (!isPrevCalculation) {
    console.log(`[MacroAnalysis] Calculating parameters for ${market}`, { 
      vix: isKr ? (indicators.vkospi?.value ?? indicators.vix?.value) : indicators.vix?.value,
      spread: spreadValue,
      spreadSource,
    });
  } else {
    console.debug(`[MacroAnalysis] Calculating previous baseline parameters for ${market} (delta comparison)`);
  }
  
  // 1. Risk Score Calculation (0-100)
  // Logic: Higher VIX/VKOSPI, Inverted Yield Curve, High Inflation, Negative Market Momentum = Higher Risk
  let riskScore = DASHBOARD_CONFIG.DEFAULT_RISK_SCORE; // Slightly bullish default baseline

  // Volatility component (Weight: 25%)
  // US VIX baseline ~15, KR VKOSPI baseline ~18-20
  const vix = isKr ? (indicators.vkospi?.value || indicators.vix?.value || 20) : (indicators.vix?.value || 15);
  const vixThreshold = isKr ? 28 : 25; 
  const vixNeutral = isKr ? 20 : 14;

  if (vix > vixThreshold) riskScore += (vix - vixThreshold) * 2.5 + 12;
  else if (vix > (vixThreshold - 6)) riskScore += (vix - (vixThreshold - 6)) * 1.5;
  else if (vix < vixNeutral) riskScore -= 6;

  // Yield Spread component (Weight: 20%)
  // US (10Y-2Y), KR (10Y-3Y)
  const spread = isKr ? (indicators.government?.krYieldSpread?.value || 0.35) : (indicators.yieldSpread?.value ?? 0.47);
  if (spread < -0.1) riskScore += 28; // KR yield inversion is extremely rare and serious
  else if (spread < 0.05) riskScore += 18;
  else if (isKr && spread < 0.15) riskScore += 12; // KR spread is naturally narrower
  else if (spread < 0.25) riskScore += 6;

  // Credit Risk (KR specific - Weight: 15%)
  if (isKr && indicators.government?.krCreditSpread) {
    const cs = indicators.government.krCreditSpread.value;
    if (cs > 1.2) riskScore += 20; 
    else if (cs > 0.9) riskScore += 12;
    else if (cs > 0.65) riskScore += 6;
    else if (cs < 0.45) riskScore -= 3;
  }

  // FX Risk (KR specific - Weight: 15%)
  if (isKr && indicators.usdkrw) {
    const fx = indicators.usdkrw.value;
    const fxChange = indicators.usdkrw.change || 0;
    if (fx > 1420) riskScore += 25; 
    else if (fx > 1380) riskScore += 15;
    else if (fx > 1340) riskScore += 8;
    else if (fx < 1280) riskScore -= 5;
    
    if (fxChange > 1.2) riskScore += 12; 
    else if (fxChange > 0.7) riskScore += 6;
  }

  // Export Growth (KR specific - Weight: 10%)
  if (isKr && indicators.government?.krExports) {
    const exports = indicators.government.krExports.value;
    if (exports < -15) riskScore += 22; 
    else if (exports < -5) riskScore += 14;
    else if (exports < 2) riskScore += 7;
    else if (exports > 10) riskScore -= 5;
  }

  // Inflation component (Weight: 10%)
  const inflation = isKr ? (indicators.government?.krCpi?.value || 2.8) : (indicators.inflation?.value || 2.0);
  const inflationThreshold = isKr ? 3.2 : 3.5;
  if (inflation > 4.5) riskScore += 18;
  else if (inflation > inflationThreshold) riskScore += 10;
  else if (isKr && inflation > 2.5) riskScore += 4;
  else if (isKr && inflation < 2.0) riskScore -= 2;

  // Market Momentum (Weight: 15%)
  const marketChange = isKr 
    ? ((indicators.kospi?.change || 0) * 0.7 + (indicators.kosdaq?.change || 0) * 0.3) 
    : (indicators.sp500?.change || 0);
  
  if (marketChange < -3.5) riskScore += 22;
  else if (marketChange < -1.8) riskScore += 14;
  else if (marketChange < -0.4) riskScore += 6;
  else if (marketChange > 1.2) riskScore -= 6;

  // Clamp risk score to 0-100
  riskScore = Math.min(100, Math.max(5, Math.round(riskScore)));

  // 2. Regime Detection
  let liquidity: MacroState['regimes']['liquidity'] = 'Neutral';
  if (isKr) {
    const krM2Change = indicators.government?.krM2?.change || 0;
    if (krM2Change > 0.3) liquidity = 'Easing';
    else if (krM2Change < -0.1) liquidity = 'Tightening';
  } else {
    const m2Change = indicators.government?.m2MoneySupply?.change || indicators.government?.m2?.change || indicators.m2MoneySupply?.change || 0;
    const tgaChange = indicators.government?.tgaBalance?.change || 0;
    if (m2Change > 0.2 || tgaChange < -2) liquidity = 'Easing';
    else if (m2Change < -0.1 || tgaChange > 5) liquidity = 'Tightening';
  }

  let credit: MacroState['regimes']['credit'] = 'Flat';
  if (isKr) {
    const krCredit = indicators.government?.krCreditSpread?.value || 0.45;
    if (krCredit > 0.8) credit = 'Contracting';
    else if (krCredit > 0.55) credit = 'Expanding';
  } else {
    if (spread < -0.5) credit = 'Contracting';
    else if (spread < 0) credit = 'Expanding';
  }

  let policy: MacroState['regimes']['policy'] = 'Normal';
  if (isKr) {
    const rate = indicators.government?.bokRate?.value || 3.5;
    const cpi = indicators.government?.krCpi?.value || 3.0;
    if (rate > cpi + 1.5) policy = 'Restrictive';
    else if (rate < cpi) policy = 'Expansive';
  } else {
    const rate = indicators.fedFunds?.value || 5.25;
    const cpi = indicators.inflation?.value || 3.2;
    if (rate > cpi + 2) policy = 'Restrictive';
    else if (rate < cpi + 0.5) policy = 'Expansive';
  }

  // 3. Macro Weight (Multiplier for equity exposure, baseline 1.0x)
  const macroWeight = parseFloat(Math.max(0.4, Math.min(1.5, 1.6 - (riskScore / 100) * 1.2)).toFixed(2));

  // 4. Portfolio Risk Budget
  const portfolioRiskBudgetPct = parseFloat(Math.max(8.0, Math.min(25.0, 26 - (riskScore / 100) * 16)).toFixed(1));

  // 5. Trim Threshold
  const trimThresholdPct = parseFloat(Math.max(3.0, Math.min(12.0, 13 - (riskScore / 100) * 10)).toFixed(1));

  // 6. Determine Market Regime
  let marketRegime: MacroState['marketRegime'] = 'Neutral';
  
  const inflationVal = isKr ? (indicators.government?.krCpi?.value || 3.0) : (indicators.inflation?.value || 3.2);
  const growthVal = isKr ? (indicators.government?.krExports?.value || 5.0) : (indicators.economics?.pmiManufacturing?.value || 50);
  
  // Growth threshold: US PMI 50, KR Exports 0%
  const isHighGrowth = isKr ? growthVal > 0 : growthVal >= 50;
  // Inflation threshold: ~2.5%
  const isHighInflation = inflationVal > 2.5;

  if (isHighGrowth && !isHighInflation) marketRegime = 'Goldilocks';
  else if (isHighGrowth && isHighInflation) marketRegime = 'Reflation';
  else if (!isHighGrowth && isHighInflation) marketRegime = 'Stagflation';
  else if (!isHighGrowth && !isHighInflation) marketRegime = 'Deflation';

  let riskScoreChange = 0;
  let macroWeightChange = 0;
  let portfolioRiskBudgetPctChange = 0;
  let trimThresholdPctChange = 0;

  if (!isPrevCalculation && indicators) {
    try {
      const prevIndicators = getPreviousIndicators(indicators);
      const prevParams = calculateMacroParameters(prevIndicators, market, true);
      if (prevParams && prevParams.riskMetrics) {
        riskScoreChange = riskScore - (prevParams.riskMetrics.riskScore || 0);
        macroWeightChange = parseFloat((macroWeight - (prevParams.riskMetrics.macroWeight || 0)).toFixed(2));
        portfolioRiskBudgetPctChange = parseFloat((portfolioRiskBudgetPct - (prevParams.riskMetrics.portfolioRiskBudgetPct || 0)).toFixed(1));
        trimThresholdPctChange = parseFloat((trimThresholdPct - (prevParams.riskMetrics.trimThresholdPct || 0)).toFixed(1));
      }
    } catch (err) {
      console.warn('[MacroAnalysis] Failed to calculate previous macro parameters:', err);
    }
  }

  const riskMetrics = {
    riskScore,
    macroWeight,
    portfolioRiskBudgetPct,
    trimThresholdPct,
    updatedAt: new Date().toISOString(),
    riskScoreChange,
    macroWeightChange,
    portfolioRiskBudgetPctChange,
    trimThresholdPctChange
  };

  return {
    riskMetrics,
    marketRegime,
    regimes: {
      liquidity,
      credit,
      policy
    }
  };
};
